import $ from 'jquery';
import _ from 'underscore';

import * as benchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileStates from 'FileStates';
import navigateToURL from 'lib/navigateToURL';
import LinkActions from 'link-preview/LinkActions';
import LinkStore from 'link-preview/LinkStore';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';

function buildUrlMap(node: Element): {[url: string]: number} {
  const urlMap: {[url: string]: number} = {};
  for (const a of Array.from($(node).find('a[href]'))) {
    urlMap[a.getAttribute('href')!] = 1;
  }
  return urlMap;
}

const CUSTOM_NAVIGATION_FILE_TYPES = [ModelTypeConstants.LOCATION];

function openInNewTab(url: string): void {
  try {
    window.open(url, '_blank', 'noopener noreferrer');
  } catch (e) {
    if (e instanceof DOMException && e.name === 'SyntaxError') {
      notifications.error('The link could not be opened. Please check that the target URL is correct.');
    } else {
      throw e;
    }
  }
}

/**
 * Opens the URL within Benchling if the URL was processed by the LinkStore.
 * Otherwise, opens the URL in a new tab.
 *
 * Note: This method directly accesses the LinkStore so it should NOT be used in a render method,
 * only in event handlers.
 * @param  {String} url
 */
export function openLink(url: string): void {
  const fileId = LinkStore.getFileIdForUrl(url);
  const relativeURL = benchlingUtil.getRelativeURL(url);

  if (fileId == null || relativeURL == null) {
    openInNewTab(url);
  } else {
    const fileState = FileStates.get().getFileState(fileId);
    const fileType = APIUtil.apiIdToType(fileId);
    if (fileState != null && !CUSTOM_NAVIGATION_FILE_TYPES.includes(fileType)) {
      const alignment = benchlingUtil.parseUri(url).queryKey.alignment;
      FileBrowserActions.openItem(fileId, alignment ? {alignment} : undefined);
      return;
    }
    if (fileState == null) {
      // Entity types without client-side FileState support (e.g. chat sessions) have no
      // matching client-side route, so navigateToURL would hit noOpRoute and do nothing.
      // Open in a new tab instead so the server handles routing and access control.
      openInNewTab(url);
      return;
    }
    navigateToURL(relativeURL);
  }
}

export function linkComponentDidMount(domNode: Element): {[url: string]: number} {
  const urlMap = buildUrlMap(domNode);
  const added = (() => {
    const result = [];
    for (const url of Object.keys(urlMap || {})) {
      result.push(url);
    }
    return result;
  })();
  const removed: Array<string> = [];
  // Avoid triggering an action if there is an action that is causing this component to be mounted.
  _.defer(() => {
    LinkActions.syncLinks(added, removed);
  });
  return urlMap;
}

export function linkComponentDidUpdate(
  domNode: Element,
  urlMap: {[url: string]: number}
): {[url: string]: number} {
  // Note: This is run on every keystroke, so it should be fast.
  //       Even with 20 links in the note part, this ran in ~0.1ms.
  let url;
  const nextUrlMap = buildUrlMap(domNode);
  const added = (() => {
    const result = [];
    for (url of Object.keys(nextUrlMap || {})) {
      // eslint-disable-next-line benchlint/no-in-operator
      if (!(url in urlMap)) {
        result.push(url);
      }
    }
    return result;
  })();
  const removed = (() => {
    const result1 = [];
    for (url of Object.keys(urlMap || {})) {
      // eslint-disable-next-line benchlint/no-in-operator
      if (!(url in nextUrlMap)) {
        result1.push(url);
      }
    }
    return result1;
  })();
  // Avoid triggering an action if there is an action that is causing this component to be updated.
  _.defer(() => {
    LinkActions.syncLinks(added, removed);
  });

  return nextUrlMap;
}

export function linkComponentWillUnmount(domNode: Element): void {
  const urlMap = buildUrlMap(domNode);
  const added: Array<string> = [];
  const removed = (() => {
    const result = [];
    for (const url of Object.keys(urlMap || {})) {
      result.push(url);
    }
    return result;
  })();
  // Avoid triggering an action if there is an action that is causing this component to be updated.
  _.defer(() => {
    LinkActions.syncLinks(added, removed);
  });
}
